if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initProductMediaPreload();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.initThumbnailDrag();
        this.initThumbnailOverflow();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initProductMediaPreload() {
        const slides = Array.from(this.elements.viewer?.querySelectorAll('.product__media-item') || []);
        const images = slides.map((slide) => slide.querySelector('img')).filter(Boolean);
        if (images.length < 2) return;

        const promoteImage = (image, priority = 'auto') => {
          if (!image || image.dataset.galleryPreloaded === 'true') return;
          image.dataset.galleryPreloaded = 'true';
          image.loading = 'eager';
          image.fetchPriority = priority;
        };
        const warmAdjacentImages = (slideIndex = 0) => {
          for (let index = slideIndex; index <= slideIndex + 3; index += 1) {
            const slide = slides[index];
            promoteImage(slide?.querySelector('img'), 'high');
          }
        };
        const getCurrentSlideIndex = () => {
          const activeSlide = this.elements.viewer.querySelector('.product__media-item.is-active');
          return Math.max(0, slides.indexOf(activeSlide));
        };
        const preloadRemainingImages = () => {
          let index = 1;
          const preloadNext = () => {
            if (!this.isConnected || index >= images.length) return;
            promoteImage(images[index], 'low');
            index += 1;
            window.setTimeout(preloadNext, 70);
          };
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(preloadNext, { timeout: 250 });
          } else {
            window.setTimeout(preloadNext, 100);
          }
        };
        const startPreloading = () => {
          warmAdjacentImages(0);
          preloadRemainingImages();
        };

        const firstMediaImage = images[0];
        if (firstMediaImage.complete) window.setTimeout(startPreloading);
        else firstMediaImage.addEventListener('load', startPreloading, { once: true });

        this.elements.viewer.addEventListener('pointerdown', () => warmAdjacentImages(getCurrentSlideIndex()), {
          passive: true,
        });
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          warmAdjacentImages(Math.max(0, slides.indexOf(event.detail.currentElement)));
        });
      }

      initThumbnailDrag() {
        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let startScrollLeft = 0;
        let hasDragged = false;
        let suppressNextClick = false;

        slider.addEventListener('pointerdown', (event) => {
          if (!event.isPrimary || event.pointerType === 'touch' || (event.pointerType === 'mouse' && event.button !== 0)) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          startScrollLeft = slider.scrollLeft;
          hasDragged = false;
          suppressNextClick = false;
        });

        slider.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointerId) return;
          const distanceX = event.clientX - startX;
          const distanceY = event.clientY - startY;
          if (!hasDragged && Math.abs(distanceY) > Math.abs(distanceX)) return;
          if (Math.abs(distanceX) > 3 && !hasDragged) {
            hasDragged = true;
            suppressNextClick = true;
            slider.setPointerCapture(pointerId);
          }
          if (!hasDragged) return;
          event.preventDefault();
          slider.classList.add('is-dragging');
          slider.scrollLeft = startScrollLeft - distanceX;
        });

        const stopThumbnailDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          if (slider.hasPointerCapture(pointerId)) slider.releasePointerCapture(pointerId);
          pointerId = null;
          slider.classList.remove('is-dragging');
          hasDragged = false;
        };

        const suppressThumbnailClick = (event) => {
          if (!suppressNextClick) return;
          event.preventDefault();
          event.stopPropagation();
          suppressNextClick = false;
        };

        slider.addEventListener('pointerup', stopThumbnailDrag);
        slider.addEventListener('pointercancel', stopThumbnailDrag);
        window.addEventListener('pointerup', stopThumbnailDrag);
        window.addEventListener('pointercancel', stopThumbnailDrag);
        slider.addEventListener('click', suppressThumbnailClick, true);
      }

      initThumbnailOverflow() {
        const rail = this.elements.thumbnails;
        const slider = rail.slider;
        if (!slider) return;

        const updateOverflow = () => {
          if (!this.mql.matches) {
            rail.classList.remove('is-overflowing', 'is-at-end');
            return;
          }
          const isOverflowing = slider.scrollHeight > slider.clientHeight + 1;
          const isAtEnd = slider.scrollTop + slider.clientHeight >= slider.scrollHeight - 1;
          rail.classList.toggle('is-overflowing', isOverflowing);
          rail.classList.toggle('is-at-end', !isOverflowing || isAtEnd);
        };

        slider.addEventListener('scroll', updateOverflow, { passive: true });
        this.mql.addEventListener('change', updateOverflow);
        new ResizeObserver(updateOverflow).observe(slider);
        window.requestAnimationFrame(updateOverflow);
      }

      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;
        if (this.pendingMediaId && currentMedia.dataset.mediaId !== this.pendingMediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const previousMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (this.mql.matches && previousMedia && previousMedia !== activeMedia) {
          const previousIndex = mediaItems.indexOf(previousMedia);
          const activeIndex = mediaItems.indexOf(activeMedia);
          const directionClass =
            activeIndex > previousIndex ? 'media-slide-in-from-right' : 'media-slide-in-from-left';
          activeMedia.classList.remove('media-slide-in-from-left', 'media-slide-in-from-right');
          void activeMedia.offsetWidth;
          activeMedia.classList.add(directionClass);
          activeMedia.addEventListener('animationend', () => activeMedia.classList.remove(directionClass), { once: true });
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.pendingMediaId = activeMedia.dataset.mediaId;
        clearTimeout(this.pendingMediaTimeout);
        this.pendingMediaTimeout = setTimeout(() => {
          this.pendingMediaId = null;
        }, 700);
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.mql.matches) {
          const slider = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleBottom = slider.scrollTop + slider.clientHeight;
          if (thumbnailTop < slider.scrollTop) slider.scrollTo({ top: thumbnailTop });
          if (thumbnailBottom > visibleBottom) slider.scrollTo({ top: thumbnailBottom - slider.clientHeight });
          window.requestAnimationFrame(() => slider.dispatchEvent(new Event('scroll')));
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
